import { http } from "./client";
import type { PageParams } from "@/types/api";
import type {
  Candidate,
  CandidateAssessment,
  CandidateConversionResult,
  CandidateMatch,
  CandidateProfile,
  CandidateSource,
  CandidateStage,
  Interview,
  InterviewFeedback,
  InterviewStatus,
  JobOffer,
  JobRequisition,
  OfferApproval,
  OfferStatus,
  RecruitmentDashboardStats,
  RecruitmentReport,
  RequisitionApproval,
  RequisitionPriority,
  RequisitionStatus,
  WorkMode,
} from "@/types/domain";

// ---------------------------------------------------------------- filters

export interface RequisitionFilters extends PageParams {
  status?: RequisitionStatus | "";
  priority?: RequisitionPriority | "";
  department_id?: string;
  hiring_manager_id?: string;
  search?: string;
}

export interface CandidateFilters extends PageParams {
  stage?: CandidateStage | "";
  source?: CandidateSource | "";
  requisition_id?: string;
  assigned_recruiter_id?: string;
  department_id?: string;
  search?: string;
  active_only?: boolean;
}

export interface InterviewFilters extends PageParams {
  status?: InterviewStatus | "";
  candidate_id?: string;
  requisition_id?: string;
  interviewer_id?: string;
  from?: string;
  to?: string;
  /** Scheduled/rescheduled interviews from today onwards, soonest first. */
  upcoming?: boolean;
}

/** Dashboard / report filters. from/to are inclusive YYYY-MM-DD dates on
 * when the application was created. */
export interface RecruitmentStatsFilters {
  from?: string;
  to?: string;
  requisition_id?: string;
  department_id?: string;
}

export interface AssessmentFilters extends PageParams {
  status?: string;
  candidate_id?: string;
}

export interface OfferFilters extends PageParams {
  status?: OfferStatus | "";
  candidate_id?: string;
  requisition_id?: string;
}

// ---------------------------------------------------------------- inputs

export interface RequisitionInput {
  department_id?: string;
  designation: string;
  hiring_manager_id?: string;
  openings_count?: number;
  employment_type: string;
  work_location?: string;
  work_mode?: WorkMode;
  salary_min?: number | null;
  salary_max?: number | null;
  currency?: string;
  job_title: string;
  job_description?: string;
  required_skills?: string[];
  preferred_skills?: string[];
  required_experience_years?: number;
  education_requirements?: string;
  priority?: RequisitionPriority;
  business_justification?: string;
  /** YYYY-MM-DD; "" clears. */
  application_deadline?: string;
  target_hiring_date?: string;
}

export interface CandidateInput {
  full_name: string;
  email: string;
  phone?: string;
  alternate_phone?: string;
  location?: string;
  skills?: string[];
  experience_years?: number;
  education?: string;
  current_company?: string;
  current_designation?: string;
  expected_salary?: number | null;
  notice_period_days?: number;
  source?: CandidateSource;
  requisition_id?: string;
  assigned_recruiter_id?: string;
  notes?: string;
}

export interface ScheduleInterviewInput {
  candidate_id: string;
  requisition_id?: string;
  round_number?: number;
  interview_type?: string;
  scheduled_date: string;
  start_time: string;
  end_time: string;
  location_or_link?: string;
  notes?: string;
  participant_ids?: string[];
}

export interface AssessmentInput {
  candidate_id: string;
  requisition_id?: string;
  assessment_name: string;
  assessment_type?: string;
  due_date?: string;
}

export interface OfferInput {
  candidate_id: string;
  requisition_id: string;
  position: string;
  employment_type: string;
  joining_date: string;
  base_salary?: number;
  allowances?: number;
  bonus?: number;
  benefits?: string;
  currency?: string;
  probation_period_months?: number;
  notice_period_days?: number;
  offer_expiry_date: string;
  terms?: string;
}

export interface ConvertCandidateInput {
  /** Empty = auto-numbered with the company's employee code prefix. */
  employee_code?: string;
  /** Monthly basic (monthly salary type); empty = accepted offer's annual base / 12. */
  basic_salary?: number;
  daily_rate?: number;
  hourly_rate?: number;
  department_id?: string;
  manager_id?: string;
  designation?: string;
  work_location?: string;
  joining_date?: string;
  salary_type?: string;
  employment_type?: string;
}

export const recruitmentApi = {
  // ---- requisitions ----
  listRequisitions: (filters: RequisitionFilters = {}) => http.getPage<JobRequisition>("/recruitment/requisitions", filters),
  getRequisition: (id: string) => http.get<JobRequisition>(`/recruitment/requisitions/${id}`),
  createRequisition: (input: RequisitionInput) => http.post<JobRequisition>("/recruitment/requisitions", input),
  updateRequisition: (id: string, input: Partial<RequisitionInput>) => http.put<JobRequisition>(`/recruitment/requisitions/${id}`, input),
  submitRequisition: (id: string) => http.post<JobRequisition>(`/recruitment/requisitions/${id}/submit`, {}),
  approveRequisition: (id: string, comments?: string) => http.put<JobRequisition>(`/recruitment/requisitions/${id}/approve`, { comments }),
  rejectRequisition: (id: string, comments?: string) => http.put<JobRequisition>(`/recruitment/requisitions/${id}/reject`, { comments }),
  transitionRequisition: (id: string, status: RequisitionStatus, reason?: string) =>
    http.put<JobRequisition>(`/recruitment/requisitions/${id}/status`, { status, reason }),
  requisitionApprovals: (id: string) => http.get<RequisitionApproval[]>(`/recruitment/requisitions/${id}/approvals`),

  // ---- candidates ----
  listCandidates: (filters: CandidateFilters = {}) => http.getPage<Candidate>("/recruitment/candidates", filters),
  getCandidate: (id: string) => http.get<CandidateProfile>(`/recruitment/candidates/${id}`),
  findDuplicates: (params: { email?: string; phone?: string; requisition_id?: string; exclude_id?: string }) =>
    http.get<CandidateMatch[]>("/recruitment/candidates/duplicates", params),
  createCandidate: (input: CandidateInput) => http.post<Candidate>("/recruitment/candidates", input),
  updateCandidate: (id: string, input: Partial<CandidateInput>) => http.put<Candidate>(`/recruitment/candidates/${id}`, input),
  deleteCandidate: (id: string) => http.delete<void>(`/recruitment/candidates/${id}`),
  moveCandidateStage: (id: string, stage: CandidateStage, reason?: string) =>
    http.put<Candidate>(`/recruitment/candidates/${id}/stage`, { stage, reason }),
  uploadResume: (id: string, file: File) => {
    const form = new FormData();
    form.set("resume", file);
    return http.post<Candidate>(`/recruitment/candidates/${id}/resume`, form);
  },
  resumeUrl: (id: string) => `/recruitment/candidates/${id}/resume`,
  /** Resume bytes via the authenticated client (a plain <a href> carries no token). */
  downloadResume: (id: string) => http.download(`/recruitment/candidates/${id}/resume`),
  convertCandidate: (id: string, input: ConvertCandidateInput) =>
    http.post<CandidateConversionResult>(`/recruitment/candidates/${id}/convert`, input),

  // ---- interviews ----
  listInterviews: (filters: InterviewFilters = {}) => http.getPage<Interview>("/recruitment/interviews", filters),
  getInterview: (id: string) => http.get<Interview>(`/recruitment/interviews/${id}`),
  scheduleInterview: (input: ScheduleInterviewInput) => http.post<Interview>("/recruitment/interviews", input),
  updateInterview: (
    id: string,
    input: Partial<{ status: InterviewStatus; interview_type: string; scheduled_date: string; start_time: string; end_time: string; location_or_link: string; notes: string; participant_ids: string[] }>,
  ) =>
    http.put<Interview>(`/recruitment/interviews/${id}`, input),
  submitFeedback: (
    id: string,
    input: Partial<Pick<InterviewFeedback, "technical_rating" | "communication_rating" | "experience_rating" | "culture_fit_rating" | "overall_rating" | "comments">> &
      Pick<InterviewFeedback, "recommendation">,
  ) => http.post<InterviewFeedback>(`/recruitment/interviews/${id}/feedback`, input),

  // ---- assessments ----
  listAssessments: (filters: AssessmentFilters = {}) => http.getPage<CandidateAssessment>("/recruitment/assessments", filters),
  getAssessment: (id: string) => http.get<CandidateAssessment>(`/recruitment/assessments/${id}`),
  createAssessment: (input: AssessmentInput) => http.post<CandidateAssessment>("/recruitment/assessments", input),
  updateAssessment: (id: string, input: Partial<{ status: string; score: number; result: string; comments: string }>) =>
    http.put<CandidateAssessment>(`/recruitment/assessments/${id}`, input),

  // ---- offers ----
  listOffers: (filters: OfferFilters = {}) => http.getPage<JobOffer>("/recruitment/offers", filters),
  getOffer: (id: string) => http.get<JobOffer>(`/recruitment/offers/${id}`),
  myOffer: () => http.get<JobOffer>("/employee/self/offer"),
  createOffer: (input: OfferInput) => http.post<JobOffer>("/recruitment/offers", input),
  updateOffer: (id: string, input: Partial<OfferInput>) => http.put<JobOffer>(`/recruitment/offers/${id}`, input),
  submitOffer: (id: string) => http.post<JobOffer>(`/recruitment/offers/${id}/submit`, {}),
  approveOffer: (id: string, comments?: string) => http.put<JobOffer>(`/recruitment/offers/${id}/approve`, { comments }),
  rejectOffer: (id: string, comments?: string) => http.put<JobOffer>(`/recruitment/offers/${id}/reject`, { comments }),
  sendOffer: (id: string) => http.post<JobOffer>(`/recruitment/offers/${id}/send`, {}),
  recordOfferResponse: (id: string, accept: boolean, reason?: string) =>
    http.post<JobOffer>(`/recruitment/offers/${id}/response`, { accept, reason }),
  withdrawOffer: (id: string, comments?: string) => http.post<JobOffer>(`/recruitment/offers/${id}/withdraw`, { comments }),
  offerApprovals: (id: string) => http.get<OfferApproval[]>(`/recruitment/offers/${id}/approvals`),

  // ---- dashboard ----
  dashboard: (filters: RecruitmentStatsFilters = {}) => http.get<RecruitmentDashboardStats>("/recruitment/dashboard", filters),
  report: (filters: RecruitmentStatsFilters = {}) => http.get<RecruitmentReport>("/recruitment/reports", filters),
};
